import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Badge, Button, Modal, Form, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { Icon, iconLoaded } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney } from '../lib/format.js';

// ─────────────────────────────────────────────────────────────────────────
// Type-Metadaten: pro feature_type Icon, Label, Default-Wert + Schema.
// ─────────────────────────────────────────────────────────────────────────

const TYPE_META = {
  boolean:      { label: 'An/Aus',         icon: 'tabler:toggle-right',  color: 'primary'   },
  quota:        { label: 'Mengen-Limit',   icon: 'tabler:number',        color: 'success'   },
  scope:        { label: 'Whitelist',      icon: 'tabler:list-check',    color: 'secondary' },
  rate_limit:   { label: 'Rate-Limit',     icon: 'tabler:gauge',         color: 'danger'    },
  time_window:  { label: 'Zeit-Fenster',   icon: 'tabler:calendar-time', color: 'warning'   },
  version_lock: { label: 'Version-Lock',   icon: 'tabler:tag',           color: 'primary'   },
  custom:       { label: 'Custom',         icon: 'tabler:code',          color: 'secondary' }
};

const PERIOD_LABEL = { one_time: 'einmalig', monthly: '/Monat', yearly: '/Jahr' };

// Schema + Default pro Feature-Type. `enabled` im Default heisst Grundumfang:
// licenseService.injectEnabledDefaults gibt das Feature mit diesem Wert JEDEM
// Kunden ohne Buchung, der Shop zeigt es als inklusive. Neue Features starten
// deshalb mit enabled:false; der Schalter "Im Grundumfang" setzt es bewusst.
// Bestellte Lizenzen setzen enabled selbst (licenseConfiguratorService._buildValue).
const TYPE_DEFAULTS = {
  boolean: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled'],
               properties: { enabled: { type: 'boolean' } } },
    default: { enabled: false }
  },
  quota: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled', 'limit'],
               properties: { enabled: { type: 'boolean' }, limit: { type: 'integer', minimum: 0 } } },
    default: { enabled: false, limit: 10 }
  },
  scope: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled', 'allowed'],
               properties: { enabled: { type: 'boolean' }, allowed: { type: 'array', items: { type: 'string' }, uniqueItems: true } } },
    default: { enabled: false, allowed: [] }
  },
  rate_limit: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled', 'count', 'per_unit'],
               properties: { enabled: { type: 'boolean' }, count: { type: 'integer', minimum: 1 },
                             per_unit: { type: 'string', enum: ['sec', 'min', 'hour', 'day'] } } },
    default: { enabled: false, count: 60, per_unit: 'min' }
  },
  time_window: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled'],
               properties: { enabled: { type: 'boolean' }, starts_at: { type: 'string' }, ends_at: { type: 'string' } } },
    default: { enabled: false }
  },
  version_lock: {
    schema:  { type: 'object', additionalProperties: false, required: ['enabled'],
               properties: { enabled: { type: 'boolean' }, min_version: { type: 'string' }, max_version: { type: 'string' } } },
    default: { enabled: false }
  },
  custom: {
    schema:  { type: 'object', additionalProperties: true },
    default: {}
  }
};

const RATE_UNITS = [
  { value: 'sec',  label: 'Sekunde' },
  { value: 'min',  label: 'Minute' },
  { value: 'hour', label: 'Stunde' },
  { value: 'day',  label: 'Tag' }
];

// ─────────────────────────────────────────────────────────────────────────
// Typed Default-Editor: pro feature_type echtes UI statt JSON.
// ─────────────────────────────────────────────────────────────────────────

function ScopeListEditor({ value, onChange }) {
  const items = Array.isArray(value?.allowed) ? value.allowed : [];
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || items.includes(t)) return;
    onChange({ ...value, enabled: value?.enabled === true, allowed: [...items, t] });
    setDraft('');
  };
  return (
    <div>
      <Form.Label>Erlaubte Werte (Whitelist)</Form.Label>
      <InputGroup className="mb-2" size="sm">
        <Form.Control value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="z.B. dji_mavic_3, autel_evo" />
        <Button variant="outline-secondary" onClick={add}><Icon icon="tabler:plus" /></Button>
      </InputGroup>
      <div className="d-flex flex-wrap gap-1">
        {items.length === 0
          ? <span className="text-muted small">Noch keine Einträge.</span>
          : items.map((it, i) => (
              <Badge key={it} bg="" className="badge-soft-secondary d-flex align-items-center" style={{ fontSize: '0.85em' }}>
                {it}
                <Icon icon="tabler:x" className="ms-1" style={{ cursor: 'pointer' }}
                  onClick={() => onChange({ ...value, enabled: value?.enabled === true, allowed: items.filter((_, j) => j !== i) })} />
              </Badge>
            ))
        }
      </div>
    </div>
  );
}

/**
 * Schalter fuer default_value.enabled: im Grundumfang heisst, jeder Kunde hat
 * das Feature ohne Buchung (injectEnabledDefaults); der Shop zeigt es als
 * inklusive, boolean-Features sind dann nicht bestellbar.
 */
function GrundumfangSchalter({ featureType, value, onChange }) {
  const an = value?.enabled === true;
  const hilfe = featureType === 'boolean'
    ? 'Jeder Kunde hat das Feature ohne Buchung. Im Shop sichtbar als „Inklusive“, nicht bestellbar.'
    : featureType === 'quota'
      ? 'Jeder Kunde ohne eigene Buchung hat das Standard-Limit. Im Shop als „N inklusive“, weitere Mengen buchbar; eine Buchung ersetzt das Standard-Limit.'
      : 'Jeder Kunde ohne eigene Buchung erhält den Standardwert.';
  return (
    <Form.Group className="mb-3">
      <Form.Check type="switch" id="feature-grundumfang" checked={an}
        label="Im Grundumfang (Standard für alle Kunden aktiv)"
        onChange={(e) => onChange({ ...(value || {}), enabled: e.target.checked })} />
      <Form.Text>{hilfe}</Form.Text>
    </Form.Group>
  );
}

function DefaultValueEditor({ featureType, value, onChange }) {
  const v = value || {};

  if (featureType === 'boolean') {
    return <GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} />;
  }

  if (featureType === 'quota') {
    return (
      <>
        <GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} />
        <Form.Group>
          <Form.Label>Standard-Limit</Form.Label>
          <Form.Control type="number" min="0" value={v.limit ?? 10}
            onChange={(e) => onChange({ ...v, limit: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
          <Form.Text>Menge im Grundumfang (wenn aktiv) und Vorgabe, wenn der Konfigurator das Feature ohne Menge aufnimmt.</Form.Text>
        </Form.Group>
      </>
    );
  }

  if (featureType === 'scope') {
    return (
      <>
        <GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} />
        <ScopeListEditor value={v} onChange={onChange} />
      </>
    );
  }

  if (featureType === 'rate_limit') {
    return (
      <Row className="g-2">
        <Col xs={12}><GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} /></Col>
        <Col xs={5}>
          <Form.Label>Anzahl</Form.Label>
          <Form.Control type="number" min="1" value={v.count ?? 60}
            onChange={(e) => onChange({ ...v, count: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
        </Col>
        <Col xs={4}>
          <Form.Label>pro</Form.Label>
          <Form.Select value={v.per_unit ?? 'min'}
            onChange={(e) => onChange({ ...v, per_unit: e.target.value })}>
            {RATE_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
          </Form.Select>
        </Col>
      </Row>
    );
  }

  if (featureType === 'time_window') {
    return (
      <Row className="g-2">
        <Col xs={12}><GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} /></Col>
        <Col md={6}>
          <Form.Label>Gültig ab</Form.Label>
          <Form.Control type="date" value={v.starts_at ? v.starts_at.slice(0, 10) : ''}
            onChange={(e) => onChange({ ...v, starts_at: e.target.value || undefined })} />
        </Col>
        <Col md={6}>
          <Form.Label>Gültig bis</Form.Label>
          <Form.Control type="date" value={v.ends_at ? v.ends_at.slice(0, 10) : ''}
            onChange={(e) => onChange({ ...v, ends_at: e.target.value || undefined })} />
        </Col>
        <Col xs={12}>
          <Form.Text>Leer lassen wenn unbegrenzt — diese Defaults gelten wenn der Konfigurator nichts Spezifisches setzt.</Form.Text>
        </Col>
      </Row>
    );
  }

  if (featureType === 'version_lock') {
    return (
      <Row className="g-2">
        <Col xs={12}><GrundumfangSchalter featureType={featureType} value={v} onChange={onChange} /></Col>
        <Col md={6}>
          <Form.Label>Min-Version</Form.Label>
          <Form.Control value={v.min_version || ''} placeholder="z.B. 1.0.0"
            onChange={(e) => onChange({ ...v, min_version: e.target.value || undefined })} />
        </Col>
        <Col md={6}>
          <Form.Label>Max-Version</Form.Label>
          <Form.Control value={v.max_version || ''} placeholder="z.B. 2.x"
            onChange={(e) => onChange({ ...v, max_version: e.target.value || undefined })} />
        </Col>
      </Row>
    );
  }

  // custom: nur JSON
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Anzeige des Default-Werts in der Tabelle (kompakt, ohne JSON-Klammern).
// ─────────────────────────────────────────────────────────────────────────

function DefaultValueDisplay({ feature }) {
  const t = feature.feature_type;
  const v = feature.default_value || {};
  if (t === 'boolean') {
    return v.enabled === true
      ? <Badge bg="" className="badge-soft-success" title="Standard aktiv: jeder Kunde hat es ohne Buchung">Grundumfang</Badge>
      : <Badge bg="" className="badge-soft-secondary">inaktiv</Badge>;
  }
  if (t === 'quota') {
    if (v.enabled !== true) return <Badge bg="" className="badge-soft-secondary">inaktiv</Badge>;
    return <span title="Standard aktiv: jeder Kunde ohne Buchung hat diese Menge"><strong>{v.limit ?? 0}</strong> <span className="text-muted">Stk inklusive</span></span>;
  }
  if (t === 'scope') {
    const arr = v.allowed || [];
    return arr.length === 0
      ? <span className="text-muted">leer</span>
      : <span>{arr.slice(0, 3).join(', ')}{arr.length > 3 ? ` +${arr.length - 3}` : ''}</span>;
  }
  if (t === 'rate_limit') return <span><strong>{v.count}</strong> <span className="text-muted">/ {v.per_unit}</span></span>;
  if (t === 'time_window') {
    if (!v.starts_at && !v.ends_at) return <span className="text-muted">unbegrenzt</span>;
    return <span className="small">{v.starts_at?.slice(0,10) || '…'} → {v.ends_at?.slice(0,10) || '…'}</span>;
  }
  if (t === 'version_lock') {
    return <span className="small">{v.min_version || '*'} – {v.max_version || '*'}</span>;
  }
  return <code className="small text-muted">{JSON.stringify(v)}</code>;
}

// ─────────────────────────────────────────────────────────────────────────
// Pricing display (in der Tabelle).
// ─────────────────────────────────────────────────────────────────────────

function PriceDisplay({ feature }) {
  if (feature.pricing_model === 'free') return <span className="text-muted">—</span>;
  if (feature.pricing_model === 'flat') {
    return <span><strong>{fmtMoney(feature.price_cents, feature.currency)}</strong> <span className="text-muted small">{PERIOD_LABEL[feature.billing_period]}</span></span>;
  }
  if (feature.pricing_model === 'per_unit') {
    return <span><strong>{fmtMoney(feature.price_cents, feature.currency)}</strong> <span className="text-muted small">/Einheit {PERIOD_LABEL[feature.billing_period]}</span></span>;
  }
  if (feature.pricing_model === 'tiered') {
    return <Badge bg="" className="badge-soft-info">gestaffelt {PERIOD_LABEL[feature.billing_period]}</Badge>;
  }
  return <span>—</span>;
}

// ─────────────────────────────────────────────────────────────────────────
// Shop-Karte: Symbol-Vorschau und Kartenbild.
// ─────────────────────────────────────────────────────────────────────────

const BILD_TYPEN = 'image/png,image/jpeg,image/webp';
const BILD_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Laedt das Kartenbild eines Features als Blob (der Abruf braucht die
 * Anmeldung) und zeigt es als Vorschau; ohne Bild nichts.
 */
function KartenBildVorschau({ featureId, imageFile }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!featureId || !imageFile) { setUrl(null); return undefined; }
    let aktiv = true;
    let objektUrl = null;
    api.get(`/features/${featureId}/image`, { responseType: 'blob' })
      .then(r => { if (!aktiv) return; objektUrl = URL.createObjectURL(r.data); setUrl(objektUrl); })
      .catch(() => { if (aktiv) setUrl(null); });
    return () => { aktiv = false; if (objektUrl) URL.revokeObjectURL(objektUrl); };
  }, [featureId, imageFile]);
  if (!url) return null;
  return <img src={url} alt="Kartenbild" className="border rounded" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }} />;
}

// ─────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────

const EMPTY_DRAFT = () => ({
  key: '',
  name: '',
  description: '',
  feature_type: 'boolean',
  license_kind: 'download',
  category: 'modules',
  icon: '',
  long_description: '',
  sort_order: '0',
  default_value: { ...TYPE_DEFAULTS.boolean.default },
  pricing_model: 'free',
  price_euro: '0',
  billing_period: 'monthly',
  currency: 'EUR',
  // Advanced (custom only)
  show_json: false,
  value_schema_text: JSON.stringify(TYPE_DEFAULTS.boolean.schema, null, 2),
  default_value_text: JSON.stringify(TYPE_DEFAULTS.boolean.default, null, 2)
});

export default function FeaturesPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canCreate = auth.can('features', 'create');
  const canUpdate = auth.can('features', 'update');

  const features = useQuery({
    queryKey: ['features-all'],
    queryFn: () => api.get('/features', { params: { include_inactive: true, with_tiers: true } }).then(r => r.data.items)
  });

  const kategorien = useQuery({
    queryKey: ['feature-categories'],
    queryFn: () => api.get('/features/categories').then(r => r.data.items),
    staleTime: Infinity,
    refetchInterval: false
  });
  const kategorieLabel = useMemo(
    () => Object.fromEntries((kategorien.data || []).map(k => [k.key, k.label])),
    [kategorien.data]
  );

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT());
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState(null);
  const [bildDatei, setBildDatei] = useState(null);
  const [bildEntfernen, setBildEntfernen] = useState(false);

  // Wenn pricing_model auf 'tiered' geht und wir editieren ein Feature: Tiers laden
  useEffect(() => {
    if (!editingId || draft.pricing_model !== 'tiered') return;
    api.get(`/features/${editingId}/tiers`).then(r => {
      setTiers((r.data.items || []).map(t => ({
        min_qty: t.min_qty,
        max_qty: t.max_qty,
        unit_price_euro: (t.unit_price_cents / 100).toFixed(2).replace('.', ',')
      })));
    }).catch(() => setTiers([{ min_qty: 1, max_qty: 10, unit_price_euro: '5,00' }]));
  }, [editingId, draft.pricing_model]);

  const open = (f) => {
    if (f) {
      setEditingId(f.id);
      setEditingImage(f.image_file || null);
      setDraft({
        key: f.key, name: f.name, description: f.description || '',
        feature_type: f.feature_type, license_kind: f.license_kind || 'download',
        category: f.category || 'sonstiges',
        icon: f.icon || '',
        long_description: f.long_description || '',
        sort_order: String(f.sort_order ?? 0),
        default_value: f.default_value || { ...TYPE_DEFAULTS[f.feature_type].default },
        pricing_model: f.pricing_model || 'free',
        price_euro: ((f.price_cents || 0) / 100).toFixed(2).replace('.', ','),
        billing_period: f.billing_period || 'monthly',
        currency: f.currency || 'EUR',
        show_json: f.feature_type === 'custom',
        value_schema_text: JSON.stringify(f.value_schema, null, 2),
        default_value_text: JSON.stringify(f.default_value, null, 2)
      });
    } else {
      setEditingId(null);
      setEditingImage(null);
      setDraft(EMPTY_DRAFT());
      setTiers([{ min_qty: 1, max_qty: 10, unit_price_euro: '5,00' }]);
    }
    setBildDatei(null);
    setBildEntfernen(false);
    setError(null);
    setShowModal(true);
  };
  const close = () => setShowModal(false);

  // Type-Wechsel: Default-Wert + Schema aus Template übernehmen
  const setType = (t) => {
    const tpl = TYPE_DEFAULTS[t] || TYPE_DEFAULTS.custom;
    setDraft(d => ({
      ...d,
      feature_type: t,
      default_value: { ...tpl.default },
      show_json: t === 'custom',
      value_schema_text: JSON.stringify(tpl.schema, null, 2),
      default_value_text: JSON.stringify(tpl.default, null, 2)
    }));
  };

  // Wenn typed Editor den default_value ändert, JSON-Mirror aktualisieren
  const setDefault = (val) => {
    setDraft(d => ({ ...d, default_value: val, default_value_text: JSON.stringify(val, null, 2) }));
  };

  const _euroToCents = (s) => {
    const n = parseFloat(String(s).replace(/\s|€|EUR/gi, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  };

  const save = useMutation({
    mutationFn: async () => {
      let value_schema, default_value;
      if (draft.show_json || draft.feature_type === 'custom') {
        try { value_schema  = JSON.parse(draft.value_schema_text); }
        catch { throw new Error('value_schema ist kein gültiges JSON'); }
        try { default_value = JSON.parse(draft.default_value_text); }
        catch { throw new Error('default_value ist kein gültiges JSON'); }
      } else {
        value_schema  = TYPE_DEFAULTS[draft.feature_type].schema;
        default_value = draft.default_value;
      }

      const body = {
        key: draft.key,
        name: draft.name,
        description: draft.description || null,
        feature_type: draft.feature_type,
        license_kind: draft.license_kind || 'download',
        category: draft.category || 'sonstiges',
        icon: draft.icon.trim() || null,
        long_description: draft.long_description.trim() || null,
        sort_order: Math.max(0, parseInt(draft.sort_order, 10) || 0),
        value_schema,
        default_value,
        pricing_model: draft.pricing_model,
        price_cents: _euroToCents(draft.price_euro),
        billing_period: draft.billing_period,
        currency: draft.currency || 'EUR'
      };
      const r = editingId
        ? await api.patch(`/features/${editingId}`, body)
        : await api.post('/features', body);
      const featureId = r.data.feature.id;

      if (draft.pricing_model === 'tiered') {
        const tiersBody = tiers
          .filter(t => t.min_qty)
          .map(t => ({
            min_qty: parseInt(t.min_qty, 10),
            max_qty: t.max_qty == null || t.max_qty === '' ? null : parseInt(t.max_qty, 10),
            unit_price_cents: _euroToCents(t.unit_price_euro)
          }));
        if (tiersBody.length === 0) throw new Error('Mindestens ein Tier nötig wenn pricing_model = tiered');
        await api.put(`/features/${featureId}/tiers`, { tiers: tiersBody });
      }

      if (bildDatei) {
        const fd = new FormData();
        fd.append('file', bildDatei);
        await api.post(`/features/${featureId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (bildEntfernen && editingImage) {
        await api.delete(`/features/${featureId}/image`);
      }
      return r.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['features-all'] }); close(); },
    onError: (e) => setError(e.response?.data?.message
      || (e.response?.data?.details && JSON.stringify(e.response.data.details))
      || e.message)
  });

  const bildGewaehlt = (e) => {
    const datei = e.target.files && e.target.files[0];
    if (!datei) return;
    if (datei.size > BILD_MAX_BYTES) {
      setError('Das Bild ist größer als 2 MB.');
      e.target.value = '';
      return;
    }
    setBildDatei(datei);
    setBildEntfernen(false);
  };

  const iconName = draft.icon.trim();
  const iconUnbekannt = iconName !== '' && !iconLoaded(`tabler:${iconName}`);

  const setActive = useMutation({
    mutationFn: ({ id, is_active }) => api.patch(`/features/${id}`, { is_active }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['features-all'] })
  });

  const canDelete = auth.can('features', 'delete');
  const del = useMutation({
    mutationFn: (id) => api.delete(`/features/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['features-all'] }),
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  return (
    <>
      <PageHeader title="Feature-Katalog"
        subtitle="Definitionen aller lizensierbaren Features — pro Typ ein eigener Editor (kein JSON nötig)"
        actions={canCreate && <Button onClick={() => open(null)}><Icon icon="tabler:plus" className="me-1" />Neues Feature</Button>} />

      <Card className="card-flat">
        <Card.Body className="p-0">
          {features.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {features.data && features.data.length === 0 && (
            <div className="empty-state p-4 text-center text-muted">
              <Icon icon="tabler:adjustments-off" style={{ fontSize: 48 }} className="d-block mb-2 opacity-50" />
              <div>Keine Features im Katalog.</div>
            </div>
          )}
          {features.data && features.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Typ</th>
                  <th>Default</th>
                  <th>Preis</th>
                  <th>Kategorie</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {features.data.map(f => {
                  const meta = TYPE_META[f.feature_type] || TYPE_META.custom;
                  return (
                    <tr key={f.id} style={canUpdate ? { cursor: 'pointer' } : {}}
                        onClick={() => canUpdate && open(f)}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Icon icon={f.icon ? `tabler:${f.icon}` : meta.icon} className={`me-2 text-${meta.color}`} style={{ fontSize: 20, flexShrink: 0 }} />
                          <div>
                            <div className="fw-semibold">{f.name}</div>
                            <code className="small text-muted">{f.key}</code>
                            {f.description && <div className="text-muted small">{f.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td><Badge bg="" className={`badge-soft-${meta.color}`}>{meta.label}</Badge></td>
                      <td><DefaultValueDisplay feature={f} /></td>
                      <td><PriceDisplay feature={f} /></td>
                      <td>{f.category_label || kategorieLabel[f.category] || f.category || <span className="text-muted">—</span>}</td>
                      <td>{f.is_active
                        ? <Badge bg="" className="badge-soft-success">aktiv</Badge>
                        : <Badge bg="" className="badge-soft-secondary">inaktiv</Badge>}</td>
                      <td className="text-end pe-3" onClick={(e) => e.stopPropagation()}>
                        {canUpdate && (
                          <>
                            <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => open(f)} title="Bearbeiten">
                              <Icon icon="tabler:edit" />
                            </Button>
                            <Button size="sm" variant="outline-secondary" className="me-1" title={f.is_active ? 'Deaktivieren' : 'Aktivieren'}
                              onClick={() => setActive.mutate({ id: f.id, is_active: !f.is_active })}>
                              <Icon icon={f.is_active ? 'tabler:eye-off' : 'tabler:eye'} />
                            </Button>
                          </>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="outline-danger" title="Endgültig löschen"
                            onClick={() => {
                              if (confirm(`Feature "${f.name}" (${f.key}) endgültig löschen?\n\nGeht nur wenn das Feature in keiner Lizenz mehr verwendet wird.`)) del.mutate(f.id);
                            }}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* ─── Modal ─── */}
      <Modal show={showModal} onHide={close} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? `Feature bearbeiten: ${draft.key}` : 'Neues Feature'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          {!editingId && (
            <Alert variant="info" className="py-2 small">
              <Icon icon="tabler:info-circle" className="me-1" />
              Neue Features werden <strong>inaktiv</strong> angelegt — sie sind erst dann
              bestellbar, wenn du sie in der Liste explizit aktivierst (Augen-Icon).
              Bestehende Lizenzen mit diesem Feature bleiben davon unberührt.
            </Alert>
          )}

          {/* Stammdaten */}
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Key *</Form.Label>
                <Form.Control required value={draft.key} disabled={!!editingId}
                  onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                  placeholder="z.B. streaming.max_drones" />
                <Form.Text>Eindeutiger Bezeichner. Klein, mit Punkt/Underscore — nach Anlage nicht mehr änderbar.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={7}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control required value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="z.B. Maximale Drohnen pro Stream" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Typ</Form.Label>
                <Form.Select value={draft.feature_type} onChange={(e) => setType(e.target.value)} disabled={!!editingId}>
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <option key={k} value={k}>{m.label} ({k})</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Lizenz-Art</Form.Label>
                {/* Werte bleiben 'download'/'api' (Backend-CHECK-Constraint license_kind);
                    nur die Beschriftung nennt sie so, wie der Operator denkt (TOFU vs API).
                    'download' = TOFU/Edge-Offline-Token (bindet an EINE Box, auto-revoke nach
                    Bind); 'api' = langlebiger Online-Key (refreshbar ueber die Laufzeit). */}
                <Form.Select value={draft.license_kind} onChange={(e) => setDraft({ ...draft, license_kind: e.target.value })}>
                  <option value="download">TOFU (Edge / Offline, einmalige Bindung)</option>
                  <option value="api">API (kontinuierlich / Online)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Kategorie</Form.Label>
                <Form.Select value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                  {(kategorien.data || []).map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                  {kategorien.data && !kategorien.data.some(k => k.key === draft.category) && (
                    <option value={draft.category}>{draft.category} (nicht in der Liste)</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control as="textarea" rows={2} value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Was tut dieses Feature? Wofür wird es lizensiert?" />
              </Form.Group>
            </Col>
          </Row>

          {/* ─── Shop-Karte ─── */}
          <hr />
          <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
            <Icon icon="tabler:layout-cards" className="me-1" />Shop-Karte
          </h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Symbol (Tabler-Name)</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ minWidth: 42, justifyContent: 'center' }}>
                    <Icon icon={`tabler:${iconName && !iconUnbekannt ? iconName : 'puzzle'}`} style={{ fontSize: 18 }} />
                  </InputGroup.Text>
                  <Form.Control value={draft.icon} isInvalid={iconUnbekannt}
                    onChange={(e) => setDraft({ ...draft, icon: e.target.value.toLowerCase() })}
                    placeholder="z.B. truck, map-2, video" />
                  <Form.Control.Feedback type="invalid">Kein Tabler-Symbol mit diesem Namen.</Form.Control.Feedback>
                </InputGroup>
                <Form.Text>Name ohne „tabler:“, siehe tabler.io/icons. Leer: Platzhalter-Symbol.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Reihenfolge</Form.Label>
                <Form.Control type="number" min="0" value={draft.sort_order}
                  onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
                <Form.Text>Innerhalb der Kategorie, aufsteigend.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Bild (optional)</Form.Label>
                <Form.Control type="file" accept={BILD_TYPEN} onChange={bildGewaehlt} />
                <Form.Text>PNG, JPEG oder WebP bis 2 MB. Ersetzt das Symbol auf der Karte.</Form.Text>
                {editingImage && !bildDatei && (
                  <div className="mt-2 d-flex align-items-start gap-2 flex-wrap">
                    {!bildEntfernen && <KartenBildVorschau featureId={editingId} imageFile={editingImage} />}
                    <Form.Check type="switch" id="feature-bild-entfernen" label="Bild entfernen"
                      checked={bildEntfernen} onChange={(e) => setBildEntfernen(e.target.checked)} />
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Langbeschreibung</Form.Label>
                <Form.Control as="textarea" rows={4} value={draft.long_description}
                  onChange={(e) => setDraft({ ...draft, long_description: e.target.value })}
                  placeholder="Erscheint im Detailfenster des Shops. Absätze mit Leerzeile." />
              </Form.Group>
            </Col>
          </Row>

          {/* ─── Default-Wert: typed UI ─── */}
          <hr />
          <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
            <Icon icon="tabler:adjustments" className="me-1" />Standard-Konfiguration
          </h6>
          {!draft.show_json && draft.feature_type !== 'custom' && (
            <DefaultValueEditor featureType={draft.feature_type} value={draft.default_value} onChange={setDefault} />
          )}
          {(draft.show_json || draft.feature_type === 'custom') && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">JSON-Schema</Form.Label>
                  <Form.Control as="textarea" rows={6} className="font-monospace small"
                    value={draft.value_schema_text}
                    onChange={(e) => setDraft({ ...draft, value_schema_text: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Default-Wert (JSON)</Form.Label>
                  <Form.Control as="textarea" rows={6} className="font-monospace small"
                    value={draft.default_value_text}
                    onChange={(e) => setDraft({ ...draft, default_value_text: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
          )}
          {draft.feature_type !== 'custom' && (
            <Form.Check className="mt-2" type="switch" checked={draft.show_json}
              onChange={(e) => setDraft({ ...draft, show_json: e.target.checked })}
              label="JSON-Schema manuell bearbeiten (Power-User)" />
          )}

          {/* ─── Pricing ─── */}
          <hr />
          <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
            <Icon icon="tabler:cash" className="me-1" />Pricing
          </h6>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Pricing-Modell</Form.Label>
                <Form.Select value={draft.pricing_model}
                  onChange={(e) => setDraft({ ...draft, pricing_model: e.target.value })}>
                  <option value="free">free — kostenlos</option>
                  <option value="flat">flat — Pauschalpreis</option>
                  <option value="per_unit">per_unit — Preis × Menge</option>
                  <option value="tiered">tiered — gestaffelt nach Menge</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {(draft.pricing_model === 'flat' || draft.pricing_model === 'per_unit') && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Preis ({draft.pricing_model === 'per_unit' ? 'pro Einheit' : 'pauschal'})</Form.Label>
                  <InputGroup>
                    <Form.Control value={draft.price_euro}
                      onChange={(e) => setDraft({ ...draft, price_euro: e.target.value })}
                      placeholder="9,99" />
                    <InputGroup.Text>{draft.currency}</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            )}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Abrechnung</Form.Label>
                <Form.Select value={draft.billing_period}
                  onChange={(e) => setDraft({ ...draft, billing_period: e.target.value })}
                  disabled={draft.pricing_model === 'free'}>
                  <option value="one_time">einmalig</option>
                  <option value="monthly">monatlich</option>
                  <option value="yearly">jährlich</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {draft.pricing_model === 'tiered' && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="small">Staffel-Tiers</strong>
                <Button size="sm" variant="outline-secondary"
                  onClick={() => {
                    const last = tiers[tiers.length - 1];
                    const nextMin = last ? (last.max_qty ? Number(last.max_qty) + 1 : (Number(last.min_qty) + 10)) : 1;
                    setTiers([...tiers, { min_qty: nextMin, max_qty: nextMin + 9, unit_price_euro: '0,00' }]);
                  }}>
                  <Icon icon="tabler:plus" /> Tier hinzufügen
                </Button>
              </div>
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>von Menge</th>
                    <th style={{ width: 110 }}>bis Menge</th>
                    <th style={{ width: 160 }}>Preis pro Einheit</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <Form.Control size="sm" type="number" min="1" value={t.min_qty}
                          onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, min_qty: e.target.value } : x))} />
                      </td>
                      <td>
                        <Form.Control size="sm" type="number" min="1"
                          value={t.max_qty == null ? '' : t.max_qty}
                          placeholder="∞ (offen)"
                          onChange={(e) => {
                            const v = e.target.value === '' ? null : e.target.value;
                            setTiers(tiers.map((x, j) => j === i ? { ...x, max_qty: v } : x));
                          }} />
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Form.Control value={t.unit_price_euro}
                            onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, unit_price_euro: e.target.value } : x))} />
                          <InputGroup.Text>{draft.currency}</InputGroup.Text>
                        </InputGroup>
                      </td>
                      <td className="text-end align-middle">
                        <Button size="sm" variant="outline-danger" onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                          disabled={tiers.length === 1}>
                          <Icon icon="tabler:x" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Form.Text className="d-block mt-1">
                Lückenlos aufsteigend — letzter Tier kann <code>bis Menge</code> leer lassen für „unbegrenzt".
              </Form.Text>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} disabled={!draft.key || !draft.name || save.isLoading}>
            {save.isLoading ? 'Speichere…' : editingId ? 'Speichern' : 'Anlegen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
